import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Facebook, Phone, Mail, MapPin, ShieldCheck } from "lucide-react";
import { companyDetails } from "../data";

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [company, setCompany] = useState(companyDetails);

  useEffect(() => {
    fetch("https://api.dev.auxo.dk/public/content/virksomhedsoplysninger")
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (data && data.length > 0) {
          const item = data[0];
          setCompany({
            name: item.firmanavn?.da || item.firmanavn || companyDetails.name,
            cvr: item.cvr || companyDetails.cvr,
            address: item.adresse?.da || item.adresse || companyDetails.address,
            phone: item.telefon || companyDetails.phone,
            email: item.email || companyDetails.email,
            facebook: item.facebook_url || companyDetails.facebook,
            logo: (item.logo && item.logo[0]) || companyDetails.logo
          });
        }
      })
      .catch(() => {});
  }, []);

  return (
    <footer className="bg-slate-900 text-white pt-16 pb-8 border-t-4 border-[#00D492]">
      <div className="max-w-7xl mx-auto px-4 md:px-6 grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
        {/* Brand & Description */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <img
              src="https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/225a8a96-ecd5-4478-8aa7-f0833a9ec198.png"
              alt="Køreskolen AG Logo"
              className="h-12 w-auto object-contain"
            />
          </div>
          <p className="text-slate-400 text-sm leading-relaxed mt-2">
            Drømmen om frihed på vejene begynder her hos os. Vi tilbyder professionel køreundervisning med fokus på tryghed, kvalitet og succes.
          </p>
          <div className="flex items-center gap-3 mt-2">
            <a
              href={company.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2.5 bg-slate-800 hover:bg-[#00D492] text-white rounded-full transition-all transform hover:scale-110 cursor-pointer"
              aria-label="Følg os på Facebook"
            >
              <Facebook className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Quick Links */}
        <div className="flex flex-col gap-4">
          <h3 className="text-lg font-bold text-[#00D492] tracking-wide uppercase">Hurtige Links</h3>
          <ul className="grid grid-cols-2 gap-2 text-sm text-slate-400">
            <li>
              <Link to="/" className="hover:text-white transition-colors">Forside</Link>
            </li>
            <li>
              <Link to="/priser" className="hover:text-white transition-colors">Priser</Link>
            </li>
            <li>
              <Link to="/holdstart" className="hover:text-white transition-colors">Holdstart</Link>
            </li>
            <li>
              <Link to="/materialer" className="hover:text-white transition-colors">Materialer</Link>
            </li>
            <li>
              <Link to="/nyheder" className="hover:text-white transition-colors">Nyheder</Link>
            </li>
            <li>
              <Link to="/vores-ydelser" className="hover:text-white transition-colors">Vores ydelser</Link>
            </li>
            <li>
              <Link to="/foerstehjaelp" className="hover:text-white transition-colors">Førstehjælp</Link>
            </li>
            <li>
              <Link to="/om-os" className="hover:text-white transition-colors">Om os</Link>
            </li>
            <li>
              <Link to="/kontakt" className="hover:text-white transition-colors">Kontakt</Link>
            </li>
          </ul>
        </div>

        {/* Contact Info */}
        <div className="flex flex-col gap-4">
          <h3 className="text-lg font-bold text-[#00D492] tracking-wide uppercase">Virksomhedsoplysninger</h3>
          <ul className="space-y-3 text-sm text-slate-400">
            <li className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[#00D492] shrink-0 mt-0.5" />
              <span>{company.address}</span>
            </li>
            <li className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-[#00D492] shrink-0" />
              <a href={`tel:${company.phone.replace(/\s+/g, "")}`} className="hover:text-white transition-colors whitespace-nowrap">
                {company.phone}
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-[#00D492] shrink-0" />
              <a href={`mailto:${company.email}`} className="hover:text-white transition-colors whitespace-nowrap">
                {company.email}
              </a>
            </li>
            <li className="flex items-center gap-3 pt-2 border-t border-slate-800">
              <ShieldCheck className="w-5 h-5 text-[#00D492] shrink-0" />
              <span>CVR: {company.cvr}</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom Copyright */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-8 border-t border-slate-800 text-center text-xs text-slate-500 flex flex-col sm:flex-row justify-between items-center gap-4">
        <p>© {currentYear} Køreskolen AG. Alle rettigheder forbeholdes.</p>
        <p className="flex items-center gap-1">
          Udviklet med <span>❤️</span> i Silkeborg
        </p>
      </div>
    </footer>
  );
}
